// see: https://html.spec.whatwg.org/multipage/dom.html#phrasing-content
// html <p> allows phrasing content
const phrasingNodeNames = `
A,ABBR,AREA,AUDIO,B,BDI,BDO,BR,BUTTON,CANVAS,CITE,CODE,DATA,DATALIST,DEL,DFN,EM,EMBED,I,IFRAME,IMG,INPUT,INS,KBD,LABEL,LINK,MAP,MARK,META,METER,NOSCRIPT,OBJECT,OUTPUT,PICTURE,PROGRESS,Q,RUBY,S,SAMP,SCRIPT,SELECT,SLOT,SMALL,SPAN,STRONG,SUB,SUP,TEMPLATE,TEXTAREA,TIME,U,VAR,VIDEO,WBR
`
    .trim()
    .split(',');
const isPhrasingNode = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }
    return phrasingNodeNames.includes(node.nodeName);
};
const isMsoPasteData = (doc) => {
    if (doc.querySelector('.MsoNormal') ||
        /v:imagedata/.test(doc.body.innerHTML)) {
        return true;
    }
    return false;
};
const getRTFData = (event) => {
    var _a;
    const data = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData('text/rtf');
    return data;
};
const convertHexToBase64 = (str) => {
    const hhList = str.match(/../g);
    if (!hhList) {
        return '';
    }
    return btoa(hhList.map((hh) => String.fromCharCode(parseInt(hh, 16))).join(''));
};
const getPictureType = (segment) => {
    if (segment.includes('\\png')) {
        return 'image/png';
    }
    if (segment.includes('\\jpeg')) {
        return 'image/jpeg';
    }
    return '';
};
export const selectRTFPictures = (rtf) => {
    // https://interoperability.blob.core.windows.net/files/Archive_References/[MSFT-RTF].pdf Page148-151
    const pictureHeaderRe = /\{\\\*\\shppict\s*\{\\pict[\s\S]+?\{\\\*\\blipuid\s+[0-9a-z]+\}\s*/i;
    const pictureDataRe = /[0-9a-z\s]+/i;
    const pictureTrailerRe = /\s*\}\}/;
    const pictureStructureSegmentRe = new RegExp(pictureHeaderRe.source + pictureDataRe.source + pictureTrailerRe.source, 'gi');
    const pitrureDataSegmentRe = new RegExp(`${pictureHeaderRe.source}(${pictureDataRe.source})${pictureTrailerRe.source}`, 'i');
    const segments = rtf.match(pictureStructureSegmentRe);
    const pictures = [];
    if (segments) {
        segments.forEach((segment) => {
            const type = getPictureType(segment);
            const data = segment.match(pitrureDataSegmentRe)[1];
            pictures.push({ type, data: convertHexToBase64(data) });
        });
    }
    return pictures;
};
const replaceMsoImageSrc = (doc, rtf) => {
    const pictures = selectRTFPictures(rtf);
    // NOTE: 开启 RelyOnVML 后，vml 元素会暴露在最外层
    const images = doc.querySelectorAll(`${CSS.escape('v:imagedata')}[src], img[src]`);
    if (images.length !== pictures.length) {
        return;
    }
    images.forEach((node, idx) => {
        let image;
        if (node.tagName === 'V:IMAGEDATA') {
            image = doc.createElement('img');
            // NOTE: 未来可以把其他 vml 的属性附带到 <img> 标签上
            const vshape = node.parentNode;
            if (vshape.style.width) {
                image.width = parseInt(vshape.style.width, 10);
            }
            if (vshape.style.height) {
                image.height = parseInt(vshape.style.height, 10);
            }
            image.alt = vshape.getAttribute('alt') || '';
            vshape === null || vshape === void 0 ? void 0 : vshape.insertBefore(image, node.nextSibling);
        }
        else {
            image = node;
        }
        const info = pictures[idx];
        const src = `data:${info.type};base64,${info.data}`;
        image.setAttribute('src', src);
        image.setAttribute('data-need-to-upload', 'true');
    });
};
export const processMsoPictureData = (doc, event) => {
    if (!isMsoPasteData(doc) || !event) {
        return doc;
    }
    const rtfData = getRTFData(event);
    if (!rtfData) {
        return doc;
    }
    replaceMsoImageSrc(doc, rtfData);
    return doc;
};
const isEmptyNode = (node) => {
    var _a;
    // 没有子节点
    if (!node.hasChildNodes()) {
        return true;
    }
    let hasNodeOrText = false;
    for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE && ((_a = child.nodeValue) === null || _a === void 0 ? void 0 : _a.trim()) === '') {
            // 文字节点是空白字符
        }
        else {
            hasNodeOrText = true;
            break;
        }
    }
    // 没有正常节点
    if (!hasNodeOrText) {
        return true;
    }
    // 其他情况
    return false;
};
export const processDocumentWithImage = (doc) => {
    const root = doc.body;
    // 保存 dom 树遍历的路径
    let inlinePath = [];
    const dfs = (node) => {
        var _a, _b;
        // 标记是否处于 inline 元素中
        if (node.nodeName === 'IMG') {
            if (((_a = inlinePath[0]) === null || _a === void 0 ? void 0 : _a.nodeName) === 'P') {
                node.setAttribute('data-inline', 'true');
            }
            return node;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const isParagraphNode = node.nodeName === 'P';
            const isPhrasingContent = isPhrasingNode(node);
            if (isParagraphNode) {
                // 只处理最临近的 <p>
                inlinePath = [node];
            }
            else if (isPhrasingContent && node.nodeName !== 'IMG') {
                inlinePath.push(node);
            }
            let child = node.childNodes[0];
            while (child) {
                // NOTE: 后续支持 inline 的 <img> 后，移除这段 if 处理
                if (child.nodeName === 'IMG' &&
                    !child.dataset.exeditorInline) {
                    if (inlinePath.length > 0) {
                        // copy path
                        const path = inlinePath.slice(0);
                        // 每次复制的父节点
                        let copy = null;
                        // 指针 指向下一个节点
                        let cnode = child.nextSibling;
                        // 指针 指向父节点
                        let pnode = path.pop();
                        // 先移除 <img>
                        node.removeChild(child);
                        // 从下往上按层遍历
                        while (pnode) {
                            // 复制父节点
                            const tmp = pnode.cloneNode(false);
                            // 之前一层复制出来的父节点，放在后面
                            if (copy) {
                                tmp.appendChild(copy);
                            }
                            copy = tmp;
                            // 把在此之后的节点移动到复制出来的节点下面
                            while (cnode) {
                                const next = cnode.nextSibling;
                                copy.appendChild(cnode);
                                cnode = next;
                            }
                            // 如果没有右侧的节点，则删除复制出来的节点
                            if (isEmptyNode(copy)) {
                                copy = null;
                            }
                            // 指针移动
                            cnode = pnode.nextSibling;
                            const nextParentNode = path.pop();
                            // 如果没有左侧的节点，则删除本来的父节点
                            if (isEmptyNode(pnode)) {
                                nextParentNode === null || nextParentNode === void 0 ? void 0 : nextParentNode.removeChild(pnode);
                            }
                            // 指针移动
                            pnode = nextParentNode;
                        }
                        // 根节点的父节点
                        const path0 = inlinePath[0];
                        const rnode = path0.parentNode;
                        // 新复制出来的 dom 树
                        if (copy) {
                            rnode === null || rnode === void 0 ? void 0 : rnode.insertBefore(copy, path0.nextSibling);
                            rnode === null || rnode === void 0 ? void 0 : rnode.insertBefore(child, copy);
                        }
                        else {
                            rnode === null || rnode === void 0 ? void 0 : rnode.insertBefore(child, path0.nextSibling);
                        }
                        // 根节点如果为空
                        if (isEmptyNode(path0)) {
                            rnode === null || rnode === void 0 ? void 0 : rnode.removeChild(path0);
                            // 删除根节点之后，需要用插入的节点替换掉根节点
                            inlinePath[0] = child;
                        }
                        // 此时后方已经没有节点
                        break;
                    }
                }
                // 考虑遍历路径可能会变化的问题
                child = (_b = dfs(child)) === null || _b === void 0 ? void 0 : _b.nextSibling;
            }
            if (isPhrasingContent || isParagraphNode) {
                return inlinePath.pop();
            }
        }
        return node;
    };
    dfs(root);
    return doc;
};
//# sourceMappingURL=utils.js.map